import React, { useState, useEffect, useRef } from "react";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, HomeOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space, message, Popconfirm } from "antd";
import type { InputRef, TableColumnsType, TableColumnType, TableProps } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import "./add_item_class.css";
import useSWR from "swr";

export interface ItemClassDataType {
    key: string;
    code: string;
    description: string;
}

interface AddItemClassProps {
    onSelectItemClass: (selectedClass: ItemClassDataType) => void;
}

type DataIndex = keyof ItemClassDataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);
const AddItemClass: React.FC<AddItemClassProps> = ({ onSelectItemClass }) => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [editingKey, setEditingKey] = useState<string>('');
    const [editingCode, setEditingCode] = useState<string>('');
    const [editingDescription, setEditingDescription] = useState<string>('');
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newClassCode, setNewClassCode] = useState<string>('');
    const [newClassDescription, setNewClassDescription] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);


    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const { data: itemClassRawData, error: fetchError, mutate } = useSWR<Omit<ItemClassDataType, 'key'>[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-classes`,
        fetcher
    );

    const itemClasses: ItemClassDataType[] = React.useMemo(() => {
        if (!itemClassRawData) return [];
        return itemClassRawData.map(item => ({
            ...item,
            key: item.code,
        }));
    }, [itemClassRawData]);


    const isEditing = (record: ItemClassDataType) => record.key === editingKey;
    const isAddingRecord = (record: ItemClassDataType) => record.key === 'new-class';

    const startEdit = (record: ItemClassDataType) => {
        if (isAdding || selectedRowKeys.length > 0) return;
        setEditingKey(record.key);
        setEditingCode(record.code);
        setEditingDescription(record.description);
    };

    const cancelEdit = () => {
        setEditingKey('');
        setEditingCode('');
        setEditingDescription('');
    };

    const saveEdit = async (originalCode: string) => {
        const key = `update-${originalCode}`;
        message.loading({ content: 'Menyimpan perubahan...', key });

        const codeToSave = editingCode.trim();
        const descriptionToSave = editingDescription.trim();

        if (!codeToSave || !descriptionToSave) {
            message.error({ content: 'Kode dan Nama Class tidak boleh kosong.', key, duration: 3 });
            return;
        }
        try {

            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-classes/${originalCode}`, {
                code: codeToSave,
                description: descriptionToSave
            });
            message.success({ content: 'Perubahan berhasil disimpan!', key, duration: 2 });
            setEditingKey('');
            mutate();
        } catch (err: any) {
            console.error('Gagal menyimpan perubahan:', err);
            message.error({ content: `Gagal menyimpan: ${err.response?.data?.error || err.message || 'Error tidak diketahui'}`, key, duration: 4 });
        }
    };

    const handleAdd = () => {
        if (editingKey) return;
        setIsAdding(true);
        setNewClassCode('');
        setNewClassDescription('');
        setSelectedRowKeys([]);
    };

    const handleCancelNew = () => {
        setIsAdding(false);
        setNewClassCode('');
        setNewClassDescription('');
    };

    const handleSaveNew = async () => {
        const codeToAdd = newClassCode.trim();
        const descriptionToAdd = newClassDescription.trim();

        if (!codeToAdd || !descriptionToAdd) {
            message.error('Kode dan Nama Class baru tidak boleh kosong.');
            return;
        }

        const key = `add-new`;
        message.loading({ content: 'Menyimpan data baru...', key });

        try {
            const payload = {
                code: codeToAdd,
                description: descriptionToAdd
            };
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-classes`, payload);
            message.success({ content: 'Class baru berhasil ditambahkan!', key, duration: 2 });
            setIsAdding(false);
            setNewClassCode('');
            setNewClassDescription('');
            mutate();
        } catch (err: any) {
            console.error('Gagal menyimpan class baru:', err);
            message.error({ content: `Gagal menyimpan: ${err.response?.data?.error || err.message || 'Error tidak diketahui'}`, key, duration: 4 });
        }
    };

    const handleDelete = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Tidak ada baris yang dipilih untuk dihapus.');
            return;
        }
        const deleteKey = 'delete-classes';
        message.loading({ content: 'Menghapus class...', key: deleteKey });
        try {

            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-classes`, {
                data: { codes: selectedRowKeys }
            });
            message.success({ content: `${selectedRowKeys.length} class berhasil dihapus.`, key: deleteKey, duration: 2 });
            mutate();
            setSelectedRowKeys([]);
        } catch (err: any) {
            console.error('Gagal menghapus class:', err);
            message.error({ content: `Gagal menghapus: ${err.response?.data?.error || err.message || 'Error tidak diketahui'}`, key: deleteKey, duration: 4 });
        }
    };

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        if (newSelectedRowKeys.length > 0) {
            if (editingKey) cancelEdit();
            if (isAdding) handleCancelNew();
        }
    };

    const rowSelection: TableProps<ItemClassDataType>['rowSelection'] = {
        selectedRowKeys,
        onChange: onSelectChange,

        getCheckboxProps: (record) => ({
            disabled: isEditing(record) || isAddingRecord(record),
        }),
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<ItemClassDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Cari ${dataIndex === 'code' ? 'Code Class' : 'Name Class'}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Cari
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Tutup
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase())
                : false,
        filterDropdownProps: {
            onOpenChange: open => {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
    });

    const columns: TableColumnsType<ItemClassDataType> = [
        {
            title: 'Code Class',
            dataIndex: 'code',
            key: 'code',
            width: '30%',
            ...getColumnSearchProps('code'),
            render: (text, record) => {
                const editing = isEditing(record);
                const adding = isAddingRecord(record);
                if (adding) {
                    return (
                        <Input
                            value={newClassCode}
                            onChange={(e) => setNewClassCode(e.target.value)}
                            onPressEnter={handleSaveNew}
                            placeholder="Kode Class Baru"
                            style={{ margin: '-5px 0' }}
                        />
                    );
                }
                if (editing) {
                    return (
                        <Input
                            value={editingCode}
                            onChange={(e) => setEditingCode(e.target.value)}
                            style={{ margin: '-5px 0' }}
                            onPressEnter={() => saveEdit(record.code)}
                        />
                    );
                }

                return searchedColumn === 'code' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                );
            },
        },
        {
            title: 'Name Class',
            dataIndex: 'description',
            key: 'description',
            width: '50%',
            ...getColumnSearchProps('description'),
            render: (text, record) => {
                const editing = isEditing(record);
                const adding = isAddingRecord(record);
                if (adding) {
                    return (
                        <Input
                            value={newClassDescription}
                            onChange={(e) => setNewClassDescription(e.target.value)}
                            onPressEnter={handleSaveNew}
                            placeholder="Nama Class Baru"
                            style={{ margin: '-5px 0' }}
                        />
                    );
                }
                if (editing) {
                    return (
                        <Input
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            style={{ margin: '-5px 0' }}
                            onPressEnter={() => saveEdit(record.code)}
                        />
                    );
                }

                return searchedColumn === 'description' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            width: '20%',
            render: (_, record) => {
                const editing = isEditing(record);
                const adding = isAddingRecord(record);
                if (adding) {
                    return (
                        <Space size="middle">
                            <Button type="link" icon={<CheckOutlined />} onClick={handleSaveNew} style={{ padding: 0, color: 'green' }} />
                            <Button type="link" icon={<CloseOutlined />} onClick={handleCancelNew} style={{ padding: 0, color: 'red' }} />
                        </Space>
                    );
                }
                if (editing) {
                    return (
                        <Space size="middle">
                            <Popconfirm title="Simpan perubahan?" onConfirm={() => saveEdit(record.code)} okText="Ya" cancelText="Tidak">
                                <Button type="link" icon={<CheckOutlined />} style={{ padding: 0, color: 'green' }} />
                            </Popconfirm>
                            <Button
                                type="link"
                                icon={<CloseOutlined />}
                                onClick={cancelEdit}
                                style={{ padding: 0, color: 'red' }}
                            />
                        </Space>
                    );
                }

                return (
                    <Button
                        type="link"
                        disabled={editingKey !== '' || isAdding || selectedRowKeys.length > 0}
                        onClick={() => startEdit(record)}
                        icon={<EditOutlined />}
                        style={{ padding: 0 }}
                    />
                );
            },
        },
    ];

    const displayData = isAdding
        ? [{ key: 'new-class', code: '', description: '' }, ...itemClasses]
        : itemClasses;

    const handleRowDoubleClick = (record: ItemClassDataType) => {
        if (isAddingRecord(record) || isEditing(record) || editingKey !== '' || isAdding) {
            return;
        }
        onSelectItemClass(record);
    };


    return (
        <div id="add-item-class-container" style={transformStyle}>
            <Space style={{ marginBottom: 16 }}>
                <Button
                    onClick={handleAdd}
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={isAdding || editingKey !== '' || selectedRowKeys.length > 0}
                >
                    Add Class
                </Button>
                <Popconfirm
                    title={`Hapus ${selectedRowKeys.length} class terpilih?`}
                    description="Pastikan class tidak sedang digunakan."
                    onConfirm={handleDelete}
                    okText="Ya, Hapus"
                    cancelText="Tidak"
                    disabled={selectedRowKeys.length === 0 || isAdding || editingKey !== ''}
                >
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={selectedRowKeys.length === 0 || isAdding || editingKey !== ''}
                    >
                        Delete Class
                    </Button>
                </Popconfirm>
            </Space>
            {fetchError && <div style={{ color: 'red', marginBottom: '16px' }}>Error: Gagal memuat data. {fetchError.message}</div>}

            <Table<ItemClassDataType>
                columns={columns}
                dataSource={displayData}
                rowKey="key"
                loading={!itemClassRawData && !fetchError && !isAdding}
                bordered
                size="small"
                style={{ marginTop: '20px' }}
                rowClassName="editable-row"
                rowSelection={rowSelection}
                onRow={(record) => ({
                    onDoubleClick: () => handleRowDoubleClick(record),
                })}
            />
        </div>
    );
};

export default AddItemClass;